// ===========================================
// Teacher Availability Editor Component
// ===========================================
// Configure days and time slots for teacher availability

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  useTeacherAvailability,
  useAddTeacherAvailability,
  useUpdateTeacherAvailability,
  useDeleteTeacherAvailability,
  useAvailabilityAffectedLessons,
} from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';
import type { TeacherAvailabilitySlot, AffectedLesson } from '../../api/users.api';

// ===========================================
// CONSTANTS
// ===========================================

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DEFAULT_START = '15:00';
const DEFAULT_END = '19:00';

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

/** One human-readable line per affected lesson, for display and clipboard copy. */
const formatAffectedLine = (l: AffectedLesson) =>
  `${l.date} ${formatTime(l.startTime)}–${formatTime(l.endTime)} — ${l.label}`;

/** Pull a server error message off an axios-style error, falling back to `fallback`. */
const errorMessage = (error: unknown, fallback: string): string => {
  const message =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
      : undefined;
  return message || fallback;
};

// ===========================================
// TYPES
// ===========================================

interface TeacherAvailabilityEditorProps {
  teacherId: string;
  teacherName: string;
}

interface DaySlotForm {
  checked: boolean;
  startTime: string;
  endTime: string;
}

// ===========================================
// COMPONENT
// ===========================================

const getInitialDaySlots = (): Record<number, DaySlotForm> => {
  const slots: Record<number, DaySlotForm> = {};
  for (const day of DAYS_OF_WEEK) {
    slots[day.value] = { checked: false, startTime: DEFAULT_START, endTime: DEFAULT_END };
  }
  return slots;
};

export default function TeacherAvailabilityEditor({
  teacherId,
  teacherName,
}: TeacherAvailabilityEditorProps) {
  // Roles slice 1a: deleting a window needs people_teachers.write (was .delete).
  const { can } = usePermissions();
  const canDeleteSlot = can('people_teachers', 'write');
  const { data: availabilityData, isLoading } = useTeacherAvailability(teacherId);
  const addMutation = useAddTeacherAvailability();
  const updateMutation = useUpdateTeacherAvailability();
  const deleteMutation = useDeleteTeacherAvailability();
  const affectedMutation = useAvailabilityAffectedLessons();

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [daySlots, setDaySlots] = useState<Record<number, DaySlotForm>>(getInitialDaySlots);
  const [applyToAllTime, setApplyToAllTime] = useState({ startTime: DEFAULT_START, endTime: DEFAULT_END });
  const [addError, setAddError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit dialog state — edits a single existing window in place.
  const [editSlot, setEditSlot] = useState<TeacherAvailabilitySlot | null>(null);
  const [editForm, setEditForm] = useState<{ dayOfWeek: number; startTime: string; endTime: string }>({
    dayOfWeek: 0,
    startTime: DEFAULT_START,
    endTime: DEFAULT_END,
  });
  const [editError, setEditError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteSlotId, setDeleteSlotId] = useState<string | null>(null);

  // Affected-lessons warning state — shown before an edit/delete that would
  // strand booked lessons outside the teacher's resulting availability.
  const [warning, setWarning] = useState<{
    action: 'edit' | 'delete';
    lessons: AffectedLesson[];
  } | null>(null);
  const [copied, setCopied] = useState(false);
  // Error surfaced inside the warning dialog when an overridden commit fails
  // (the originating edit/delete dialog is already closed at that point).
  const [warningError, setWarningError] = useState<string | null>(null);
  // True while we're running the affected-lessons preview for either flow.
  const checkingAffected = affectedMutation.isPending;

  const availability: TeacherAvailabilitySlot[] = availabilityData ?? [];

  // Group slots by day of week
  const slotsByDay = availability.reduce<Record<number, TeacherAvailabilitySlot[]>>(
    (acc, slot) => {
      if (!acc[slot.dayOfWeek]) {
        acc[slot.dayOfWeek] = [];
      }
      acc[slot.dayOfWeek].push(slot);
      return acc;
    },
    {}
  );

  // Derived state
  const checkedDays = DAYS_OF_WEEK.filter((d) => daySlots[d.value].checked);
  const allDaysChecked = checkedDays.length === DAYS_OF_WEEK.length;
  const someDaysChecked = checkedDays.length > 0 && !allDaysChecked;
  const hasInvalidTimes = checkedDays.some(
    (d) => daySlots[d.value].startTime >= daySlots[d.value].endTime
  );

  // Handlers
  const handleOpenAddDialog = () => {
    setAddError(null);
    setDaySlots(getInitialDaySlots());
    setApplyToAllTime({ startTime: DEFAULT_START, endTime: DEFAULT_END });
    setAddDialogOpen(true);
  };

  const handleToggleDay = (dayValue: number) => {
    setDaySlots((prev) => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], checked: !prev[dayValue].checked },
    }));
  };

  const handleDayTimeChange = (dayValue: number, field: 'startTime' | 'endTime', value: string) => {
    setDaySlots((prev) => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], [field]: value },
    }));
  };

  const handleToggleAllDays = () => {
    const newChecked = !allDaysChecked;
    setDaySlots((prev) => {
      const updated = { ...prev };
      for (const day of DAYS_OF_WEEK) {
        updated[day.value] = { ...updated[day.value], checked: newChecked };
      }
      return updated;
    });
  };

  const handleApplyTimeToAll = () => {
    setDaySlots((prev) => {
      const updated = { ...prev };
      for (const day of DAYS_OF_WEEK) {
        updated[day.value] = {
          ...updated[day.value],
          startTime: applyToAllTime.startTime,
          endTime: applyToAllTime.endTime,
        };
      }
      return updated;
    });
  };

  const handleAddSlots = async () => {
    setAddError(null);
    setIsSubmitting(true);
    try {
      for (const day of checkedDays) {
        const slot = daySlots[day.value];
        await addMutation.mutateAsync({
          teacherId,
          data: {
            dayOfWeek: day.value,
            startTime: slot.startTime,
            endTime: slot.endTime,
          },
        });
      }
      setAddDialogOpen(false);
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to add availability window.';
      setAddError(message || 'Failed to add availability window.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Run the affected-lessons preview, then either pop the warning (if any lessons
  // would be stranded) or commit immediately. Errors in the preview are
  // non-fatal — we fall through to committing rather than blocking the admin.
  const commitDelete = async () => {
    if (!deleteSlotId) return;
    setWarningError(null);
    try {
      await deleteMutation.mutateAsync({ teacherId, slotId: deleteSlotId });
      setDeleteSlotId(null);
      setWarning(null);
    } catch (error: unknown) {
      // Keep the warning dialog open and surface the failure there.
      setWarningError(errorMessage(error, 'Failed to delete availability window.'));
    }
  };

  const handleDeleteSlot = async () => {
    if (!deleteSlotId) return;
    try {
      const lessons = await affectedMutation.mutateAsync({
        teacherId,
        slotId: deleteSlotId,
        proposed: null,
      });
      if (lessons.length > 0) {
        setCopied(false);
        setWarningError(null);
        setWarning({ action: 'delete', lessons });
        return;
      }
    } catch {
      // Preview failed — proceed without the warning rather than block.
    }
    await commitDelete();
  };

  const handleOpenEditDialog = (slot: TeacherAvailabilitySlot) => {
    setEditError(null);
    setEditSlot(slot);
    setEditForm({
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
    });
  };

  const editTimeInvalid = editForm.startTime >= editForm.endTime;

  const commitEdit = async () => {
    if (!editSlot || editTimeInvalid) return;
    setEditError(null);
    setWarningError(null);
    try {
      await updateMutation.mutateAsync({
        teacherId,
        slotId: editSlot.id,
        data: {
          dayOfWeek: editForm.dayOfWeek,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
        },
      });
      setEditSlot(null);
      setWarning(null);
    } catch (error: unknown) {
      const message = errorMessage(error, 'Failed to update availability window.');
      // Surface in whichever dialog is open: the warning (if overriding) or the
      // edit dialog (direct save with no affected lessons).
      if (warning) {
        setWarningError(message);
      } else {
        setEditError(message);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editSlot || editTimeInvalid) return;
    setEditError(null);
    try {
      const lessons = await affectedMutation.mutateAsync({
        teacherId,
        slotId: editSlot.id,
        proposed: {
          dayOfWeek: editForm.dayOfWeek,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
        },
      });
      if (lessons.length > 0) {
        setCopied(false);
        setWarningError(null);
        setWarning({ action: 'edit', lessons });
        return;
      }
    } catch {
      // Preview failed — proceed without the warning rather than block.
    }
    await commitEdit();
  };

  const handleCopyAffected = async () => {
    if (!warning) return;
    const text = warning.lessons.map(formatAffectedLine).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleConfirmWarning = async () => {
    if (!warning) return;
    if (warning.action === 'edit') {
      await commitEdit();
    } else {
      await commitDelete();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6">Teaching Availability</Typography>
            <Typography variant="body2" color="text.secondary">
              Define when {teacherName} is available to teach. Classes can only be
              scheduled within these windows.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Availability
          </Button>
        </Box>

        {/* Empty state */}
        {availability.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No availability configured. This teacher can be scheduled at any time.
            Add availability windows to restrict when classes can be scheduled.
          </Alert>
        )}

        {/* Availability table */}
        {availability.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS_OF_WEEK.map((day) => {
                  const daySlots = slotsByDay[day.value] || [];
                  if (daySlots.length === 0) return null;
                  return daySlots.map((slot, index) => (
                    <TableRow key={slot.id}>
                      {index === 0 && (
                        <TableCell
                          rowSpan={daySlots.length}
                          sx={{ fontWeight: 'bold', verticalAlign: 'top' }}
                        >
                          {day.label}
                        </TableCell>
                      )}
                      <TableCell>{formatTime(slot.startTime)}</TableCell>
                      <TableCell>{formatTime(slot.endTime)}</TableCell>
                      <TableCell>
                        <Chip
                          label={slot.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={slot.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(slot)}
                          disabled={updateMutation.isPending || deleteMutation.isPending}
                          aria-label="Edit availability window"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {canDeleteSlot && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteSlotId(slot.id)}
                            disabled={deleteMutation.isPending}
                            aria-label="Delete availability window"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      </Paper>

      {/* Add Availability Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !isSubmitting && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Availability Windows</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}

            {/* Apply to all controls */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allDaysChecked}
                      indeterminate={someDaysChecked}
                      onChange={handleToggleAllDays}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="subtitle2">Select All Days</Typography>
                  }
                  sx={{ mr: 'auto' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  value={applyToAllTime.startTime}
                  onChange={(e) =>
                    setApplyToAllTime((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  value={applyToAllTime.endTime}
                  onChange={(e) =>
                    setApplyToAllTime((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleApplyTimeToAll}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Apply to All
                </Button>
              </Box>
            </Paper>

            <Divider />

            {/* Per-day rows */}
            {DAYS_OF_WEEK.map((day) => {
              const slot = daySlots[day.value];
              const timeInvalid = slot.checked && slot.startTime >= slot.endTime;
              return (
                <Box
                  key={day.value}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    opacity: slot.checked ? 1 : 0.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={slot.checked}
                        onChange={() => handleToggleDay(day.value)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ width: 80 }}>
                        {day.label}
                      </Typography>
                    }
                    sx={{ mr: 0, minWidth: 140 }}
                  />
                  <TextField
                    type="time"
                    size="small"
                    value={slot.startTime}
                    onChange={(e) => handleDayTimeChange(day.value, 'startTime', e.target.value)}
                    disabled={!slot.checked}
                    error={timeInvalid}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    to
                  </Typography>
                  <TextField
                    type="time"
                    size="small"
                    value={slot.endTime}
                    onChange={(e) => handleDayTimeChange(day.value, 'endTime', e.target.value)}
                    disabled={!slot.checked}
                    error={timeInvalid}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              );
            })}

            {hasInvalidTimes && (
              <Alert severity="warning">
                Start time must be before end time for all selected days.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSlots}
            variant="contained"
            disabled={isSubmitting || checkedDays.length === 0 || hasInvalidTimes}
          >
            {isSubmitting
              ? 'Adding...'
              : `Add${checkedDays.length > 0 ? ` (${checkedDays.length} day${checkedDays.length !== 1 ? 's' : ''})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Availability Dialog */}
      <Dialog
        open={!!editSlot}
        onClose={() => !updateMutation.isPending && setEditSlot(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Availability Window</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            <TextField
              select
              label="Day"
              size="small"
              value={editForm.dayOfWeek}
              onChange={(e) =>
                setEditForm((prev) => ({ ...prev, dayOfWeek: Number(e.target.value) }))
              }
              fullWidth
            >
              {DAYS_OF_WEEK.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                label="Start"
                type="time"
                size="small"
                value={editForm.startTime}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, startTime: e.target.value }))
                }
                error={editTimeInvalid}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                sx={{ flex: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                to
              </Typography>
              <TextField
                label="End"
                type="time"
                size="small"
                value={editForm.endTime}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, endTime: e.target.value }))
                }
                error={editTimeInvalid}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                sx={{ flex: 1 }}
              />
            </Box>
            {editTimeInvalid && (
              <Alert severity="warning">Start time must be before end time.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditSlot(null)}
            disabled={updateMutation.isPending || checkingAffected}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={updateMutation.isPending || checkingAffected || editTimeInvalid}
          >
            {checkingAffected
              ? 'Checking…'
              : updateMutation.isPending
                ? 'Saving...'
                : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteSlotId}
        onClose={() => setDeleteSlotId(null)}
      >
        <DialogTitle>Delete Availability Window</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this availability window? This may
            affect which time slots are available for scheduling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteSlotId(null)}
            disabled={deleteMutation.isPending || checkingAffected}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSlot}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending || checkingAffected}
          >
            {checkingAffected
              ? 'Checking…'
              : deleteMutation.isPending
                ? 'Deleting...'
                : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Affected-lessons warning — shown before an edit/delete strands bookings */}
      <Dialog
        open={!!warning}
        onClose={() =>
          !updateMutation.isPending && !deleteMutation.isPending && setWarning(null)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {warning?.lessons.length}{' '}
          {warning?.lessons.length === 1 ? 'lesson' : 'lessons'} would fall outside
          availability
        </DialogTitle>
        <DialogContent>
          {warningError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWarningError(null)}>
              {warningError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warning?.action === 'delete'
              ? 'Deleting this window'
              : 'This change'}{' '}
            leaves the following booked lessons outside the teacher's availability.
            They will not be cancelled — you can proceed and reschedule them, or
            cancel and adjust the window.
          </Alert>
          <Box
            sx={{
              maxHeight: 240,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              bgcolor: 'background.default',
            }}
          >
            {warning?.lessons.map((l, i) => (
              <Typography
                key={`${l.date}-${l.startTime}-${i}`}
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', py: 0.25 }}
              >
                {formatAffectedLine(l)}
              </Typography>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopyAffected}
            sx={{ mt: 1 }}
          >
            {copied ? 'Copied!' : 'Copy list'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWarning(null);
              setWarningError(null);
            }}
            disabled={updateMutation.isPending || deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmWarning}
            color={warning?.action === 'delete' ? 'error' : 'primary'}
            variant="contained"
            disabled={updateMutation.isPending || deleteMutation.isPending}
          >
            {updateMutation.isPending || deleteMutation.isPending
              ? 'Working…'
              : warning?.action === 'delete'
                ? 'Delete anyway'
                : 'Save anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
